import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';

/** Shared fetch logic for meeting research data */
export function fetchMeetingResearch(
  orgId: number,
  meetingEntityId: string,
  signal?: AbortSignal
) {
  const client = createCRMObjectClient(orgId, 'ahoy_research');
  return client.list(
    {
      associated_with_entity_id: meetingEntityId,
      properties: [
        'ahoy_summary',
        'ahoy_status',
        'ahoy_created_at',
        'ahoy_research_type',
        'ahoy_view_definition',
      ],
      order_by: '-ahoy_created_at',
    },
    { signal: signal ?? AbortSignal.timeout(10_000) }
  );
}

/** Create query config for meeting research data (row expand view) */
export function createResearchQueryConfig(
  orgId: number,
  meetingEntityId: string | undefined,
  signal?: AbortSignal
) {
  return {
    queryKey: ['meetingResearch', orgId, meetingEntityId] as const,
    queryFn: async ({ signal: querySignal }: { signal?: AbortSignal }) => {
      if (!meetingEntityId) {
        throw new Error('No meeting entity ID');
      }
      const response = await fetchMeetingResearch(
        orgId,
        meetingEntityId,
        querySignal ?? signal
      );
      return response.results;
    },
    staleTime: 5 * 60_000,
  };
}

/**
 * Shared fetch logic for meeting notes — the records that hold captured
 * meeting summaries (Granola, Zoom, Google Meet, and manually written notes).
 *
 * These are `ahoy_meeting_notes` entities, a different entity type from the
 * `ahoy_research` prep briefs above. `ahoy_meeting_notes` is the sole source
 * consulted when determining whether a meeting has no summary — see
 * `meetingNeedsSummary` below for the full rationale.
 */
export function fetchMeetingNotes(
  orgId: number,
  meetingEntityId: string,
  signal?: AbortSignal
) {
  const client = createCRMObjectClient(orgId, 'ahoy_meeting_notes');
  return client.list(
    {
      associated_with_entity_id: meetingEntityId,
      properties: ['ahoy_summary', 'ahoy_notes_source', 'ahoy_created_at'],
      order_by: '-ahoy_created_at',
    },
    { signal: signal ?? AbortSignal.timeout(10_000) }
  );
}

/** Create query config for meeting notes data */
export function createMeetingNotesQueryConfig(
  orgId: number,
  meetingEntityId: string | undefined,
  signal?: AbortSignal
) {
  return {
    queryKey: ['meetingNotes', orgId, meetingEntityId] as const,
    queryFn: async ({ signal: querySignal }: { signal?: AbortSignal }) => {
      if (!meetingEntityId) {
        throw new Error('No meeting entity ID');
      }
      const response = await fetchMeetingNotes(
        orgId,
        meetingEntityId,
        querySignal ?? signal
      );
      return response.results;
    },
    staleTime: 5 * 60_000,
  };
}

/**
 * Whether a past meeting genuinely has no summary on record.
 *
 * A summary means a captured record of what happened — an `ahoy_meeting_notes`
 * entity (Granola, Zoom, Google Meet, or a manually written note).
 * `ahoy_research` prep briefs deliberately do NOT count: they are generated
 * *before* the meeting, so their presence says nothing about whether the
 * meeting was captured.
 *
 * Reading research to answer this question was the original defect, and it was
 * wrong in both directions — meetings with a Granola summary attached were
 * told "No meeting summary yet" and handed a blank composer, while meetings
 * with only a prep brief were treated as already covered.
 *
 * Returns false while the query is unresolved: an unloaded query is not
 * evidence of absence, and claiming "no summary" on it reintroduces the same
 * false positive.
 */
export function meetingNeedsSummary(notes: unknown[] | undefined): boolean {
  if (!Array.isArray(notes)) {
    return false;
  }
  return notes.length === 0;
}

/** Grace period after a meeting ends before it's eligible for the "no summary" banner. */
export const SUMMARY_ELIGIBILITY_GRACE_MS = 60 * 60 * 1000;

export interface SummaryEligibilityInput {
  endTime: string | undefined;
  isAllDay: boolean;
  guestCount: number;
  referenceMs: number;
}

/**
 * Whether a meeting is eligible for the "no summary" banner/prompt at all:
 * not all-day, ended more than the grace period ago, and has more than one
 * guest. Deliberately does NOT decide "is this in the past" or "is this
 * today" — callers own that day/past-scoping and AND it with this result.
 */
export function isSummaryEligibleMeeting({
  endTime,
  isAllDay,
  guestCount,
  referenceMs,
}: SummaryEligibilityInput): boolean {
  return (
    !isAllDay &&
    !!endTime &&
    new Date(endTime).getTime() <= referenceMs - SUMMARY_ELIGIBILITY_GRACE_MS &&
    guestCount > 1
  );
}
